/**
 * TransferLeadsDialog — ★ 2026-10-05 (Fase 38 UI)
 *
 * Dialog admin per trasferire in massa TUTTI i lead di un agente a un altro
 * (endpoint POST /api/agents/{id}/transfer-leads). Opzioni:
 *   • riportare i lead nella colonna "Lead" della pipeline (reset_stage)
 *   • eliminare l'account sorgente (agente + utente CRM) dopo il passaggio
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { logger } from '../../lib/logger';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { ArrowRightLeft, Loader2, AlertTriangle } from 'lucide-react';

export const TransferLeadsDialog = ({ agent, agents, onClose, onDone }) => {
  const { t } = useTranslation();
  const [toAgentId, setToAgentId] = useState('');
  const [resetStage, setResetStage] = useState(true);
  const [deleteAgent, setDeleteAgent] = useState(false);
  const [loading, setLoading] = useState(false);

  const targets = useMemo(
    () => (agents || []).filter((a) => a.id !== agent?.id && a.is_active !== false),
    [agents, agent],
  );

  const close = () => {
    if (loading) return;
    setToAgentId('');
    setResetStage(true);
    setDeleteAgent(false);
    onClose?.();
  };

  const confirm = async () => {
    if (!toAgentId) {
      toast.error(t('transferDialog.targetRequired', 'Seleziona l’agente destinatario'));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/agents/${agent.id}/transfer-leads`, {
        to_agent_id: toAgentId,
        reset_stage: resetStage ? 'lead' : null,
        delete_agent: deleteAgent,
      });
      const toName = data?.to?.name || '';
      toast.success(
        t('transferDialog.success', {
          defaultValue: '{{count}} lead trasferiti a {{name}}',
          count: data?.moved ?? 0,
          name: toName,
        }),
        {
          description: deleteAgent
            ? t('transferDialog.successDeleted', 'Account sorgente eliminato (agente + utente CRM).')
            : undefined,
        },
      );
      onDone?.();
      close();
    } catch (e) {
      logger.error('TransferLeadsDialog: transfer failed', e);
      toast.error(e?.response?.data?.detail || t('transferDialog.error', 'Errore durante il trasferimento'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!agent} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-md" data-testid="transfer-leads-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-[var(--gp-gold)]" />
            {t('transferDialog.title', 'Trasferisci lead')}
          </DialogTitle>
          <DialogDescription>
            {t('transferDialog.desc', {
              defaultValue: 'Sposta TUTTI i lead di {{name}} (assegnazione diretta, Senior, Associate e organici) a un altro agente. Lo storico del passaggio viene salvato su ogni lead.',
              name: agent?.name || '',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label>{t('transferDialog.targetLabel', 'Agente destinatario')}</Label>
            <Select value={toAgentId} onValueChange={setToAgentId}>
              <SelectTrigger data-testid="transfer-leads-target-select">
                <SelectValue placeholder={t('transferDialog.targetPlaceholder', 'Seleziona l’agente…')} />
              </SelectTrigger>
              <SelectContent>
                {targets.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`transfer-target-${a.id}`}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
            <div className="space-y-0.5">
              <Label className="text-sm">{t('transferDialog.resetStageLabel', 'Riporta in colonna «Lead»')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('transferDialog.resetStageDesc', 'Tutti i lead trasferiti ripartono dalla prima colonna della pipeline.')}
              </p>
            </div>
            <Switch checked={resetStage} onCheckedChange={setResetStage} data-testid="transfer-leads-reset-stage" />
          </div>

          <div className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-900 p-3">
            <div className="space-y-0.5">
              <Label className="text-sm text-red-700 dark:text-red-400 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                {t('transferDialog.deleteAgentLabel', 'Elimina l’account sorgente')}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t('transferDialog.deleteAgentDesc', {
                  defaultValue: 'Dopo il trasferimento {{name}} viene eliminato definitivamente (agente + accesso CRM).',
                  name: agent?.name || '',
                })}
              </p>
            </div>
            <Switch checked={deleteAgent} onCheckedChange={setDeleteAgent} data-testid="transfer-leads-delete-agent" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={loading} data-testid="transfer-leads-cancel">
            {t('transferDialog.cancel', 'Annulla')}
          </Button>
          <Button
            onClick={confirm}
            disabled={loading || !toAgentId}
            className={deleteAgent ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
            data-testid="transfer-leads-confirm"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : t('transferDialog.confirm', 'Trasferisci lead')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
